import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["data-model-ready"]
const _hoisted_2 = ["src"]

import { ref, onMounted, onUnmounted } from 'vue'
import posterUrl from '@/assets/credential/credential-poster.png'
import modelUrl from '@/assets/credential/credential.glb'


export default /*@__PURE__*/_defineComponent({
  __name: 'CredentialScene',
  setup(__props) {

const host = ref<HTMLElement | null>(null)
const canvasHost = ref<HTMLElement | null>(null)
const ready = ref(false)
let disposed = false
let cleanup = () => { /* no WebGL resources yet */ }

onMounted(async () => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  try {
    const [T, { GLTFLoader }, { RoomEnvironment }] = await Promise.all([
      import('three'), import('three/examples/jsm/loaders/GLTFLoader.js'), import('three/examples/jsm/environments/RoomEnvironment.js')
    ])
    if (disposed || !canvasHost.value || !host.value) return
    const renderer = new T.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
    renderer.toneMapping = T.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.0
    renderer.setClearColor(0, 0)
    const scene = new T.Scene()
    const camera = new T.OrthographicCamera(-2.4, 2.4, 2.55, -2.55, .1, 50)
    camera.position.set(2.5, 2.4, 9); camera.lookAt(0, 0, 0)
    scene.add(new T.HemisphereLight(0xeaf3ff, 0x7b889b, .7))
    const key = new T.DirectionalLight(0xffffff, 2.2); key.position.set(-3, 7, 5); scene.add(key)
    const fill = new T.DirectionalLight(0xc5e1ff, .8); fill.position.set(4, 4, -2); scene.add(fill)
    const pmrem = new T.PMREMGenerator(renderer)
    const studio = new RoomEnvironment()
    const environment = pmrem.fromScene(studio, .06)
    scene.environment = environment.texture
    scene.environmentIntensity = .7
    studio.dispose(); pmrem.dispose()
    canvasHost.value.appendChild(renderer.domElement)
    let model: import('three').Group | null = null
    let frame = 0
    let introStart = 0
    const hasVisited = sessionStorage.getItem('credential-intro-seen') === '1'
    let targetRotation = 0
    const render = (time = 0) => {
      if (disposed || !model) return
      const elapsed = Math.min((time - introStart) / 1000, 1)
      const intro = hasVisited ? 0 : -.20 * Math.pow(1 - elapsed, 3)
      model.rotation.y += (targetRotation + intro - model.rotation.y) * .14
      renderer.render(scene, camera)
      if (elapsed < 1 || Math.abs(model.rotation.y - targetRotation) > .0005) frame = requestAnimationFrame(render)
    }
    const resize = () => {
      if (!host.value) return
      const { width, height } = host.value.getBoundingClientRect()
      if (!width || !height) return
      camera.left = -2.55 * width / height; camera.right = 2.55 * width / height
      camera.updateProjectionMatrix(); renderer.setSize(width, height)
      renderer.render(scene, camera)
    }
    const observer = new ResizeObserver(resize); observer.observe(host.value)
    const pointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || !host.value) return
      const rect = host.value.getBoundingClientRect()
      targetRotation = ((event.clientX - rect.left) / rect.width - .5) * .13
      cancelAnimationFrame(frame); frame = requestAnimationFrame(render)
    }
    const pointerLeave = () => { targetRotation = 0; cancelAnimationFrame(frame); frame = requestAnimationFrame(render) }
    host.value.addEventListener('pointermove', pointerMove)
    host.value.addEventListener('pointerleave', pointerLeave)
    cleanup = () => {
      cancelAnimationFrame(frame); observer.disconnect()
      host.value?.removeEventListener('pointermove', pointerMove); host.value?.removeEventListener('pointerleave', pointerLeave)
      scene.traverse(object => {
        const mesh = object as import('three').Mesh
        mesh.geometry?.dispose()
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
        materials.forEach(material => material?.dispose())
      })
      environment.dispose(); renderer.dispose(); renderer.domElement.remove()
    }
    renderer.domElement.addEventListener('webglcontextlost', event => { event.preventDefault(); ready.value = false; cancelAnimationFrame(frame) })
    const gltf = await new GLTFLoader().loadAsync(modelUrl)
    if (disposed) {
      gltf.scene.traverse(object => {
        const mesh = object as import('three').Mesh; mesh.geometry?.dispose()
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]; materials.forEach(m => m?.dispose())
      }); return
    }
    const center = new T.Box3().setFromObject(gltf.scene).getCenter(new T.Vector3())
    gltf.scene.position.sub(center)
    model = new T.Group(); model.add(gltf.scene); scene.add(model)
    resize(); renderer.render(scene, camera)
    ready.value = true; introStart = performance.now(); frame = requestAnimationFrame(render)
    sessionStorage.setItem('credential-intro-seen', '1')
  } catch {
    ready.value = false; cleanup()
  }
})
onUnmounted(() => { disposed = true; cleanup() })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "host",
    ref: host,
    class: "credential-scene",
    "data-model-ready": ready.value,
    "aria-hidden": "true"
  }, [
    _createElementVNode("img", {
      src: _unref(posterUrl),
      alt: "",
      class: "credential-poster",
      fetchpriority: "high"
    }, null, 8, _hoisted_2),
    _createElementVNode("div", {
      ref_key: "canvasHost",
      ref: canvasHost,
      class: _normalizeClass(["credential-live", { ready: ready.value }])
    }, null, 2)
  ], 8, _hoisted_1))
}
}

})